import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Description as PageIcon,
  Schedule as HoursIcon,
  ContactMail as ContactIcon,
  Info as AboutIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';

import { useStoreWebsite } from '../hooks/useStoreWebsite';

const getPageIcon = (pageType) => {
  switch (pageType) {
    case 'about':
      return <AboutIcon />;
    case 'contact':
      return <ContactIcon />;
    case 'hours':
      return <HoursIcon />;
    default:
      return <PageIcon />;
  }
};

const getPageDescription = (pageType) => {
  switch (pageType) {
    case 'about':
      return 'Tell customers about your store';
    case 'contact':
      return 'Contact information and location';
    case 'hours':
      return 'Store operating hours';
    default:
      return 'Custom page';
  }
};

const CMSPageList = ({ websiteId, pages, locationId }) => {
  const navigate = useNavigate();
  const { togglePagePublish, isSaving } = useStoreWebsite(locationId);

  const handleEditPage = (page) => {
    navigate(`/website/pages/${page._id}?location=${locationId}`);
  };

  const handleTogglePublish = async (pageId) => {
    await togglePagePublish(pageId);
  };

  // Sort pages: standard pages first (about, contact, hours), then custom
  const sortedPages = [...pages].sort((a, b) => {
    const order = { about: 1, contact: 2, hours: 3 };
    const orderA = order[a.page_type] || 99;
    const orderB = order[b.page_type] || 99;
    return orderA - orderB;
  });

  return (
    <div className="settings-form">
      <div className="form-section">
        <h4 className="form-section-title">Website Pages</h4>
        <p className="helper-text">
          Manage the content on your public website. Edit each page to add your store's information.
        </p>

        <div className="pages-list">
          {sortedPages.map((page) => (
            <div key={page._id} className="page-item">
              <div className="page-info">
                <div className="page-icon">
                  {getPageIcon(page.page_type)}
                </div>
                <div className="page-details">
                  <h4>{page.title}</h4>
                  <span>{getPageDescription(page.page_type)}</span>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                <div className={`page-status ${page.is_published ? 'published' : 'draft'}`}>
                  {page.is_published ? (
                    <>
                      <VisibilityIcon style={{ fontSize: 14 }} /> Published
                    </>
                  ) : (
                    <>
                      <VisibilityOffIcon style={{ fontSize: 14 }} /> Draft
                    </>
                  )}
                </div>

                <div className="page-actions">
                  <Tooltip title={page.is_published ? 'Unpublish' : 'Publish'}>
                    <IconButton
                      onClick={() => handleTogglePublish(page._id)}
                      disabled={isSaving}
                      size="small"
                    >
                      {page.is_published ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Page">
                    <IconButton onClick={() => handleEditPage(page)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
          ))}
        </div>

        {pages.length === 0 && (
          <div className="info-text" style={{ textAlign: 'center', padding: 30 }}>
            <p>No pages found. Default pages will be created when you set up your website.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CMSPageList;
